import dcmjs from 'dcmjs';
import { utils } from '@ohif/core';
import type { ImportStudyFile } from './importStudyValidation';

const { formatPN } = utils;

const EXPLICIT_LITTLE_ENDIAN = '1.2.840.10008.1.2.1';
const IMPLICIT_LITTLE_ENDIAN = '1.2.840.10008.1.2';
const EXPLICIT_BIG_ENDIAN = '1.2.840.10008.1.2.2';

function readDicomDictFromArrayBuffer(arrayBuffer: ArrayBuffer) {
  try {
    return dcmjs.data.DicomMessage.readFile(arrayBuffer);
  } catch {
    const syntaxes = [EXPLICIT_LITTLE_ENDIAN, IMPLICIT_LITTLE_ENDIAN, EXPLICIT_BIG_ENDIAN];

    for (const syntax of syntaxes) {
      try {
        const stream = new dcmjs.data.ReadBufferStream(arrayBuffer, true);
        const dict = dcmjs.data.DicomMessage.read(stream, syntax, true);
        const dicomDict = new dcmjs.data.DicomDict({});
        dicomDict.dict = dict;
        return dicomDict;
      } catch {
        continue;
      }
    }

    throw new Error('Unable to read DICOM file');
  }
}

export type ImportStudyNaturalizedDataset = Record<string, unknown> & {
  _meta?: Record<string, unknown>;
};

/**
 * Parsed DICOM instance metadata for viewer import phases.
 */
export type ImportStudyInstanceMetadata = {
  file: ImportStudyFile;
  studyInstanceUID: string;
  seriesInstanceUID: string;
  sopInstanceUID: string;
  studyDate: string;
  studyDescription: string;
  modality: string;
  patientName: string;
  /** Naturalized dcmjs dataset (same shape as local file loader). */
  dataset: ImportStudyNaturalizedDataset;
};

function getStringValue(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (Array.isArray(value) && value.length) {
    return getStringValue(value[0]);
  }

  if (typeof value === 'object' && value !== null && 'Alphabetic' in value) {
    return getStringValue((value as { Alphabetic?: unknown }).Alphabetic);
  }

  return String(value).trim();
}

/**
 * Parses a single DICOM file. Returns null for corrupted files or when
 * StudyInstanceUID is missing.
 */
export async function parseImportStudyDicomFile(
  file: ImportStudyFile
): Promise<ImportStudyInstanceMetadata | null> {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const dicomData = readDicomDictFromArrayBuffer(arrayBuffer);
    const dataset = dcmjs.data.DicomMetaDictionary.naturalizeDataset(
      dicomData.dict
    ) as ImportStudyNaturalizedDataset;

    dataset._meta = dicomData.meta
      ? dcmjs.data.DicomMetaDictionary.namifyDataset(dicomData.meta)
      : {};

    const studyInstanceUID = getStringValue(dataset.StudyInstanceUID);
    if (!studyInstanceUID) {
      return null;
    }

    return {
      file,
      studyInstanceUID,
      seriesInstanceUID: getStringValue(dataset.SeriesInstanceUID),
      sopInstanceUID: getStringValue(dataset.SOPInstanceUID),
      studyDate: getStringValue(dataset.StudyDate),
      studyDescription: getStringValue(dataset.StudyDescription),
      modality: getStringValue(dataset.Modality),
      patientName: formatPN(dataset.PatientName) || getStringValue(dataset.PatientName),
      dataset,
    };
  } catch {
    return null;
  }
}

export type ParseImportStudyDicomFilesResult =
  | { success: true; instances: ImportStudyInstanceMetadata[] }
  | { success: false; message: string };

/**
 * Parses all DICOM files, skipping corrupted entries without StudyInstanceUID.
 */
export async function parseImportStudyDicomFiles(
  files: ImportStudyFile[],
  parseFailedMessage: string
): Promise<ParseImportStudyDicomFilesResult> {
  const instances: ImportStudyInstanceMetadata[] = [];

  for (const file of files) {
    const parsed = await parseImportStudyDicomFile(file);
    if (parsed) {
      instances.push(parsed);
    }
  }

  if (!instances.length) {
    return { success: false, message: parseFailedMessage };
  }

  return { success: true, instances };
}
